import {Component} from 'react';
import PropTypes from 'prop-types';
import SimpleModal from './SimpleModal';
import {Button, Header, Icon} from 'semantic-ui-react';
import AnalyticsGateway from '../lib/AnalyticsGateway';
import './SelectCoverPageImage.css';
import CoverImageUploadContainer from './CoverImageUploadContainer';
import CoverPageLayoutContainer from './CoverPageLayoutContainer';
import halfLayoutIcon from '../images/HalfLayoutIcon.svg';
import largeLayoutIcon from '../images/LargeLayoutIcon.svg';
import {isEqual} from 'lodash';

export default class SelectCoverPageImage extends Component {
  static propTypes = {
    franchiseThumbnails: PropTypes.arrayOf(PropTypes.string).isRequired,
    otherImages: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedImage: PropTypes.shape({
      half: PropTypes.string,
      large: PropTypes.string,
    }).isRequired,
    selectedLayout: PropTypes.string.isRequired,
    propertyId: PropTypes.number.isRequired,
    reportType: PropTypes.string.isRequired,
    onSelectImage: PropTypes.func.isRequired,
    onSelectLayout: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      highlightedImage: 0,
      originalLayout: props.selectedLayout,
    };
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.otherImages, prevProps.otherImages)) {
      this.setState({highlightedImage: 0});
    }
  }

  onImageClick = (src, i) => () => {
    this.setState({highlightedImage: i});
  };

  onLayoutClick = (layout) => () => {
    this.props.onSelectLayout(layout);
  };

  onClose = () => {
    const {highlightedImage, originalLayout} = this.state;
    const {selectedImage, otherImages, onSelectImage, selectedLayout, propertyId, reportType} = this.props;

    const image = (highlightedImage === 0 ? selectedImage[selectedLayout] : otherImages[highlightedImage - 1]) || null;
    if (selectedLayout !== originalLayout || image !== selectedImage[selectedLayout]) {
      onSelectImage(selectedLayout, image);
      this.setState({originalLayout: selectedLayout});
    }
    AnalyticsGateway.sendEvent({
      eventName: 'Select Cover Page Layout',
      properties: [
        {label: 'Page', value: 'Customise Report'},
        {label: 'Report Type', value: reportType},
        {label: 'Property ID', value: propertyId},
        {label: 'Layout', value: selectedLayout},
      ]
    });
  };

  close = () => {
    this._modal.close();
    this.onClose();
  };

  logImageModalTrigger = () => {
    const {propertyId, reportType} = this.props;

    AnalyticsGateway.sendEvent({
      eventName: 'Select Layout',
      properties: [
        {label: 'Page', value: 'Customise Report'},
        {label: 'Object Context', value: 'Cover Page'},
        {label: 'Report Type', value: reportType},
        {label: 'Property ID', value: propertyId},
      ],
    });
  };

  onUpload = (imageUri) => {
    this.setState({highlightedImage: 1});
  };

  renderImages = (images, highlighted, indexOffset) => {
    const {franchiseThumbnails} = this.props;

    return images.map((src, i) => {
      const x = i + indexOffset;

      return src ? (
        <div key={x}
             style={{backgroundImage: `url(${encodeURI(src.replace(/\/resize;.*/, '/resize;m=fit;w=240;logo=false;date=false'))})`}}
             className={`select-image ${x === highlighted ? 'highlighted' : ''} ${franchiseThumbnails.includes(src) ? 'crop-image' : ''}`}
             onClick={this.onImageClick(src, x)}
        >
          {x === highlighted && (
            <Icon name="check" inverted color="orange" size="large" fitted circular bordered={false}/>
          )}
        </div>
      ) : (
        <div key={x} className={`select-image no-image ${x === highlighted ? 'highlighted' : ''}`}
             onClick={this.onImageClick(src, x)}>
          {x === highlighted && (
            <Icon name="check" inverted color="orange" size="large" fitted circular bordered={false}/>
          )}
          Don't Use Any Image
        </div>
      );
    });
  };

  render() {
    const {selectedImage, otherImages, selectedLayout} = this.props;
    const {highlightedImage} = this.state;
    const {recWidth, recHeight} = {
      half:  {recWidth: 2035, recHeight: 1362},
      large: {recWidth: 2056, recHeight: 2178},
    }[selectedLayout];
    return (
      <SimpleModal className="cover-image-modal"
                   onOpen={this.logImageModalTrigger}
                   onClose={this.onClose}
                   trigger={<CoverPageLayoutContainer name={selectedLayout} showButton/>}
                   ref={ref => this._modal = ref}
      >
        <div style={{display: 'flex', flex: 1}}>
          <div className="select-layout-content">
            <Header>Select Layout</Header>
            <div style={{display: 'flex', flexDirection: 'column', flex: 1}} className="select-layout-container">
              <div onClick={this.onLayoutClick('half')}
                   className={`select-layout half ${selectedLayout === 'half' ? 'highlighted' : ''}`}>
                <div className="title">
                  <img width="15" height="20" src={halfLayoutIcon} alt=""/>
                  Half Page Layout
                </div>
                {selectedLayout === 'half' && (
                  <Icon name="check" inverted color="orange" size="large" fitted circular bordered={false}/>
                )}
                <CoverPageLayoutContainer
                  name="half"
                  imageUri={selectedLayout === 'half' && highlightedImage ? otherImages[highlightedImage - 1] : selectedImage.half}
                  width={152} height={212}/>
              </div>
              <div onClick={this.onLayoutClick('large')}
                   className={`select-layout large ${selectedLayout === 'large' ? 'highlighted' : ''}`}>
                <div className="title">
                  <img width="15" height="20" src={largeLayoutIcon} alt=""/>
                  Large Page Layout
                </div>
                {selectedLayout === 'large' && (
                  <Icon name="check" inverted color="orange" size="large" fitted circular bordered={false}/>
                )}
                <CoverPageLayoutContainer
                  name="large"
                  imageUri={selectedLayout === 'large' && highlightedImage ? otherImages[highlightedImage - 1] : selectedImage.large}
                  width={152} height={212}/>
              </div>
            </div>
            <Button className="close" primary onClick={this.close}>Save &amp; Close</Button>
          </div>
          <div className={`select-images ${selectedLayout}`}>
            <Header>
              Select Image
              <span style={{fontSize: '12px', fontStyle: 'italic', marginLeft: 18}}>Recommended dimensions: Width {recWidth}px, Height {recHeight}px</span>
            </Header>
            <div className="images">
              <CoverImageUploadContainer onUpload={this.onUpload}/>
              {this.renderImages([selectedImage[selectedLayout]], highlightedImage, 0)}
              {this.renderImages(otherImages, highlightedImage, 1)}
              {selectedImage[selectedLayout] ? this.renderImages([null], highlightedImage, otherImages.length + 1) : null}
            </div>
          </div>
        </div>
      </SimpleModal>
    );
  }
}
